import { z } from "zod";

// Validator for userSchemas.attributes
const attributesValidator = z.object({
  id: z.string(),
  tableId: z.string().nullable(),
  valueGroupId: z.string().nullable(),
  name: z.string(),
  type: z.string(),
  unit: z.string().nullable().optional(),
  description: z.string(),
  required: z.boolean(),
  inferred: z.boolean().default(false),
  enum: z.array(z.string()).nullable(),
  attributeOrder: z.number().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// Validator for userSchemas.schemaComponents
const schemaComponentsValidator = z.object({
  id: z.string(),
  schemaId: z.string().nullable(),
  tableId: z.string().nullable(),
  valueGroupId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// Validator for userSchemas.rowKeys
const rowKeysValidator = z.object({
  id: z.string(),
  tableId: z.string().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  required: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// Validator for userSchemas.tables
const tablesValidator = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  sourceDescription: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// Validator for userSchemas.valueGroups
const valueGroupValidator = z.object({
  id: z.string(),
  name: z.string(),
  sourceDescription: z.string(),
  attributes: z.array(attributesValidator).default([]),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// Validator for userSchemas.valueGroupSheetMappings
const valueGroupSheetMappingsValidator = z.object({
  id: z.string(),
  mappingId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// Validator for userSchemas.attributeSheetMappings
const attributeSheetMappingsValidator = z.object({
  id: z.string(),
  mappingId: z.string().nullable(),
  tableSheetMappingId: z.string().nullable(),
  valueGroupSheetMappingId: z.string().nullable(),
  attributeId: z.string().nullable(),
  targetCol: z.string(),
  transformFn: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// Validator for userSchemas.rowKeySheetMappings
const rowKeySheetMappingsValidator = z.object({
  id: z.string(),
  mappingId: z.string().nullable(),
  rowKeyId: z.string().nullable(),
  rowNumber: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// Validator for userSchemas.outputMappings
const outputMappingValidator = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  schemaId: z.string().nullable(),
  state: z.enum(["draft", "published"]),
  templateKey: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// Validator for userSchemas.tableSheetMappings
const tableSheetMappingsValidator = z.object({
  id: z.string(),
  mappingId: z.string().nullable(),
  tableId: z.string().nullable(),
  dataStartRow: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// Table Validator
const tableValidator = z.object({
  id: z
    .string()
    .uuid()
    .default(() => crypto.randomUUID()),
  name: z.string(),
  description: z.string(),
  sourceDescription: z.string().nullable().optional(),
  attributes: z.array(attributesValidator).default([]),
  rowKeys: z.array(rowKeysValidator).nullable().optional(),
  rowKeyColumnId: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// Schema Validator
const schemaValidator = z.object({
  id: z.string(),
  userId: z.string().nullable().optional(),
  complexity: z.string().nullable().optional(),
  useVision: z.boolean().nullable().optional(),
  name: z.string(),
  tables: z.array(tableValidator).default([]),
  valueGroups: z.array(valueGroupValidator).default([]),
  workspaceId: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const schemaWithEmailValidator = schemaValidator.extend({
  email: z.string(),
});

export {
  attributesValidator,
  schemaComponentsValidator,
  rowKeysValidator,
  tablesValidator,
  valueGroupValidator,
  valueGroupSheetMappingsValidator,
  attributeSheetMappingsValidator,
  rowKeySheetMappingsValidator,
  outputMappingValidator,
  tableSheetMappingsValidator,
  schemaValidator,
  schemaWithEmailValidator,
  tableValidator,
};
